import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Search, CheckCircle, XCircle, Eye, IndianRupee, CreditCard, Clock,
  Filter, ChevronLeft, ChevronRight, Edit, AlertTriangle, Wallet
} from "lucide-react";

const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, json: () => Promise.resolve(data) });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    else xhr.send();
  });
};

const statusColors = {
  paid: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  pending_verification: "bg-blue-100 text-blue-700",
  rejected: "bg-red-100 text-red-700",
  completed: "bg-emerald-100 text-emerald-700",
};

const methodIcons = {
  upi: CreditCard, wallet: Wallet, cash: IndianRupee, bank_transfer: CreditCard,
};

export default function PaymentManagement({ token, isSuperAdmin = false }) {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [verifyDialog, setVerifyDialog] = useState({ open: false, payment: null });
  const [verifyNote, setVerifyNote] = useState("");
  const [modifyDialog, setModifyDialog] = useState({ open: false, payment: null });
  const [modifyAmount, setModifyAmount] = useState("");
  const [modifyStatus, setModifyStatus] = useState("");
  const [modifyNote, setModifyNote] = useState("");
  const [proofDialog, setProofDialog] = useState({ open: false, url: "" });

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const fetchOpts = { headers };
  const limit = 20;

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/payments/all?page=${page}&limit=${limit}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (methodFilter !== "all") url += `&payment_method=${methodFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await safeFetch(url, fetchOpts);
      if (res.ok) {
        const data = await res.json();
        setPayments(data.payments || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error("Operation failed:", e); }
    setLoading(false);
  }, [token, page, statusFilter, methodFilter, search]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/admin/payments/stats`, fetchOpts);
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, [token]);

  useEffect(() => { fetchPayments(); fetchStats(); }, [fetchPayments, fetchStats]);

  const handleVerify = async (status) => {
    if (!verifyDialog.payment) return;
    try {
      const res = await safeFetch(`${API}/admin/payments/${verifyDialog.payment.payment_id}/verify`, {
        method: "PUT", ...fetchOpts,
        body: JSON.stringify({ status, verification_note: verifyNote }),
      });
      if (res.ok) { toast.success(`Payment ${status}`); setVerifyDialog({ open: false, payment: null }); setVerifyNote(""); fetchPayments(); fetchStats(); }
      else toast.error("Failed");
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleModify = async () => {
    if (!modifyDialog.payment) return;
    try {
      const body = {};
      if (modifyAmount) body.amount = parseFloat(modifyAmount);
      if (modifyStatus) body.status = modifyStatus;
      if (modifyNote) body.verification_note = modifyNote;
      const res = await safeFetch(`${API}/superadmin/payments/${modifyDialog.payment.payment_id}/modify`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify(body),
      });
      if (res.ok) { toast.success("Payment modified"); setModifyDialog({ open: false, payment: null }); fetchPayments(); fetchStats(); }
      else toast.error("Failed to modify");
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPayments();
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-xl font-semibold text-slate-900" data-testid="payment-mgmt-title">Payment Management</h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-slate-500">Total</p>
            <p className="font-heading font-bold text-2xl text-slate-900" data-testid="stats-total">{stats.total_payments || 0}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-amber-600">Pending</p>
            <p className="font-heading font-bold text-2xl text-amber-600" data-testid="stats-pending">{stats.pending || 0}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-emerald-600">Verified</p>
            <p className="font-heading font-bold text-2xl text-emerald-600" data-testid="stats-verified">{stats.verified || 0}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-slate-500">Total Amount</p>
            <p className="font-heading font-bold text-xl text-slate-900">{"\u20B9"}{(stats.paid_amount || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="payment-search-input"
              placeholder="Search by Booking ID, UTR, or Name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-full"
            />
          </div>
          <Button type="submit" variant="outline" className="rounded-full" data-testid="payment-search-btn">
            <Search className="w-4 h-4" />
          </Button>
        </form>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 rounded-full" data-testid="status-filter">
            <Filter className="w-3.5 h-3.5 mr-1" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending_verification">Pending</SelectItem>
            <SelectItem value="paid">Verified</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="pending">Cash Pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={methodFilter} onValueChange={(v) => { setMethodFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 rounded-full" data-testid="method-filter">
            <SelectValue placeholder="Method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            <SelectItem value="upi">UPI</SelectItem>
            <SelectItem value="wallet">Wallet</SelectItem>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Payments Table */}
      <Card className="rounded-2xl border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold">Payment ID</TableHead>
                <TableHead className="text-xs font-semibold">Booking</TableHead>
                <TableHead className="text-xs font-semibold">Customer</TableHead>
                <TableHead className="text-xs font-semibold">Amount</TableHead>
                <TableHead className="text-xs font-semibold">Method</TableHead>
                <TableHead className="text-xs font-semibold">UTR</TableHead>
                <TableHead className="text-xs font-semibold">Status</TableHead>
                <TableHead className="text-xs font-semibold">Date</TableHead>
                <TableHead className="text-xs font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">No payments found</TableCell></TableRow>
              )}
              {payments.map((p) => {
                const MethodIcon = methodIcons[p.payment_method] || CreditCard;
                return (
                  <TableRow key={p.payment_id} data-testid={`payment-row-${p.payment_id}`} className="hover:bg-slate-50/50">
                    <TableCell className="text-xs font-mono text-slate-600">{p.payment_id}</TableCell>
                    <TableCell>
                      <div className="text-xs font-mono text-slate-700">{p.ride_id}</div>
                      {p.service_type && <div className="text-[10px] text-slate-400 capitalize">{p.service_type.replace(/_/g, " ")}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="text-xs text-slate-700">{p.user_name || "-"}</div>
                      {p.driver_name && <div className="text-[10px] text-slate-400">Driver: {p.driver_name}</div>}
                    </TableCell>
                    <TableCell className="font-heading font-semibold text-slate-900">{"\u20B9"}{p.amount}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MethodIcon className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-xs capitalize">{p.payment_method?.replace(/_/g, " ")}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs font-mono text-blue-600">{p.upi_reference || "-"}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-full text-[10px] ${statusColors[p.status] || "bg-slate-100 text-slate-600"}`}>
                        {p.status?.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {p.created_at ? new Date(p.created_at).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {p.payment_proof_url && (
                          <Button variant="ghost" size="sm" onClick={() => setProofDialog({ open: true, url: p.payment_proof_url })} className="h-7 w-7 p-0">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {(p.status === "pending_verification" || p.status === "pending") && (
                          <>
                            <Button data-testid={`approve-payment-${p.payment_id}`} variant="ghost" size="sm" onClick={() => { setVerifyDialog({ open: true, payment: p }); setVerifyNote(""); }}
                              className="h-7 px-2 text-emerald-600 hover:bg-emerald-50 text-xs">
                              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Verify
                            </Button>
                            <Button data-testid={`reject-payment-${p.payment_id}`} variant="ghost" size="sm"
                              onClick={() => { setVerifyDialog({ open: true, payment: p }); setVerifyNote(""); }}
                              className="h-7 px-2 text-red-500 hover:bg-red-50 text-xs">
                              <XCircle className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        {isSuperAdmin && (
                          <Button data-testid={`modify-payment-${p.payment_id}`} variant="ghost" size="sm"
                            onClick={() => { setModifyDialog({ open: true, payment: p }); setModifyAmount(p.amount?.toString() || ""); setModifyStatus(p.status || ""); setModifyNote(p.verification_note || ""); }}
                            className="h-7 px-2 text-blue-600 hover:bg-blue-50 text-xs">
                            <Edit className="w-3.5 h-3.5 mr-1" /> Modify
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <p className="text-xs text-slate-500">Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total}</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 w-7 p-0 rounded-full">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs text-slate-500 flex items-center px-2">{page}/{totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 w-7 p-0 rounded-full">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Verify Dialog */}
      <Dialog open={verifyDialog.open} onOpenChange={(o) => setVerifyDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-md" data-testid="verify-payment-dialog">
          <DialogHeader>
            <DialogTitle>Verify Payment</DialogTitle>
          </DialogHeader>
          {verifyDialog.payment && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Payment ID</span><span className="font-mono">{verifyDialog.payment.payment_id}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-bold">{"\u20B9"}{verifyDialog.payment.amount}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Method</span><span className="capitalize">{verifyDialog.payment.payment_method}</span></div>
                {verifyDialog.payment.upi_reference && <div className="flex justify-between"><span className="text-slate-500">UTR</span><span className="font-mono text-blue-600">{verifyDialog.payment.upi_reference}</span></div>}
                <div className="flex justify-between"><span className="text-slate-500">Customer</span><span>{verifyDialog.payment.user_name}</span></div>
              </div>
              <div>
                <Label>Verification Note (optional)</Label>
                <Textarea data-testid="verify-note-input" value={verifyNote} onChange={(e) => setVerifyNote(e.target.value)} placeholder="Add a note..." rows={2} />
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setVerifyDialog({ open: false, payment: null })} className="rounded-full">Cancel</Button>
            <Button data-testid="reject-confirm-btn" onClick={() => handleVerify("rejected")} className="bg-red-500 hover:bg-red-600 text-white rounded-full">Reject</Button>
            <Button data-testid="approve-confirm-btn" onClick={() => handleVerify("paid")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Super Admin Modify Dialog */}
      {isSuperAdmin && (
        <Dialog open={modifyDialog.open} onOpenChange={(o) => setModifyDialog(d => ({ ...d, open: o }))}>
          <DialogContent className="max-w-md" data-testid="modify-payment-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" /> Modify Payment (Super Admin)
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Amount ({"\u20B9"})</Label>
                <Input data-testid="modify-amount-input" type="number" value={modifyAmount} onChange={(e) => setModifyAmount(e.target.value)} />
              </div>
              <div>
                <Label>Status Override</Label>
                <Select value={modifyStatus} onValueChange={setModifyStatus}>
                  <SelectTrigger data-testid="modify-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="pending_verification">Pending Verification</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note</Label>
                <Textarea data-testid="modify-note-input" value={modifyNote} onChange={(e) => setModifyNote(e.target.value)} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModifyDialog({ open: false, payment: null })} className="rounded-full">Cancel</Button>
              <Button data-testid="modify-confirm-btn" onClick={handleModify} className="bg-forest hover:bg-forest/90 text-white rounded-full">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Payment Proof Preview */}
      <Dialog open={proofDialog.open} onOpenChange={(o) => setProofDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Payment Proof</DialogTitle></DialogHeader>
          <img src={proofDialog.url} alt="Payment proof" className="w-full rounded-lg" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
